import React from "react";
import { isEqual } from "lodash";
import { getOrParseDateTime } from "@cpt/shared";
import { FulfillmentContactParty } from "client/api-v2";
import type { OrderDto, SpecialLicenceTermDto, UpdateOrderDto } from "client/api-v2";
import { updateOrder } from "areas/orders/services/orderService";
import type { OrderQuantityFormRow } from "areas/orders/utils/orderQuantities";
import { toOrderQuantityInputDtos, toQuantityFormRows } from "areas/orders/utils/orderQuantities";

export type OrderDraft = Pick<
  OrderDto,
  | "planId"
  | "rateCardCode"
  | "termStartDate"
  | "paymentTerm"
  | "salesRep"
  | "resellerBillingAccountCode"
  | "purchaseOrderNumber"
  | "validUntilOverride"
  | "customAgreementReference"
  | "preapprovedLegalTermIds"
  | "specialLicenceTerms"
  | "internalNotes"
  | "requiresManualFulfillmentApproval"
> & {
  quantities: OrderQuantityFormRow[];
  // Why: Swashbuckle drops `nullable` from an optional enum, so codegen types this as non-null,
  // but a Cloud order carries no fulfilment contact.
  fulfillmentContact: FulfillmentContactParty | null;
};

export interface OrderDraftState {
  draft: OrderDraft;
  setField: <K extends keyof OrderDraft>(key: K, value: OrderDraft[K]) => void;
  isDirty: boolean;
  save: () => Promise<OrderDto | undefined>;
  reset: () => void;
  savedOrder: OrderDto;
  applyServerOrder: (order: OrderDto) => void;
  applyServerOrderKeepingEdits: (order: OrderDto) => void;
}

// Why: the response interceptor turns date-shaped members into Luxon DateTimes whatever the
// generated type says, and the API wants a plain `yyyy-MM-dd` date.
function toLocalDateString(localDate: string): string {
  return getOrParseDateTime(localDate).toISODate() ?? "";
}

function blankToNull(value: string | null): string | null {
  const trimmed = (value ?? "").trim();
  return trimmed === "" ? null : trimmed;
}

function withoutReseller(party: FulfillmentContactParty | null): FulfillmentContactParty | null {
  if (party === FulfillmentContactParty.Both) {
    return FulfillmentContactParty.EndUser;
  }
  return party === FulfillmentContactParty.Reseller ? null : party;
}

export function isBlankSpecialLicenceTerm(term: SpecialLicenceTermDto): boolean {
  return term.title.trim() === "" && term.text.trim() === "";
}

// Why: a dirty quote locks the rate card actions, and the API refuses a save with an empty term
// row, so a freshly added empty row must not count as an edit.
function draftContent(draft: OrderDraft): OrderDraft {
  return {
    ...draft,
    specialLicenceTerms: draft.specialLicenceTerms.filter((term) => !isBlankSpecialLicenceTerm(term)),
  };
}

export function toOrderDraft(order: OrderDto): OrderDraft {
  return {
    planId: order.planId,
    rateCardCode: order.rateCardCode,
    termStartDate: toLocalDateString(order.termStartDate),
    paymentTerm: order.paymentTerm,
    quantities: toQuantityFormRows(order.quantities),
    salesRep: order.salesRep,
    resellerBillingAccountCode: order.resellerBillingAccountCode,
    purchaseOrderNumber: order.purchaseOrderNumber,
    fulfillmentContact: order.fulfillmentContact ?? null,
    validUntilOverride: order.validUntilOverride === null ? null : toLocalDateString(order.validUntilOverride),
    customAgreementReference: order.customAgreementReference,
    preapprovedLegalTermIds: order.preapprovedLegalTermIds,
    specialLicenceTerms: order.specialLicenceTerms,
    internalNotes: order.internalNotes,
    requiresManualFulfillmentApproval: order.requiresManualFulfillmentApproval,
  };
}

export function toUpdateOrderDto(draft: OrderDraft, version?: string): UpdateOrderDto {
  return {
    // Why: an opaque .NET tick count the API compares exactly; reformatting it (e.g. via Luxon,
    // which keeps milliseconds only) makes every save a 409.
    version,
    planId: draft.planId.trim(),
    rateCardCode: draft.rateCardCode.trim(),
    termStartDate: draft.termStartDate,
    paymentTerm: draft.paymentTerm,
    quantities: toOrderQuantityInputDtos(draft.quantities),
    salesRep: blankToNull(draft.salesRep),
    resellerBillingAccountCode: blankToNull(draft.resellerBillingAccountCode),
    purchaseOrderNumber: blankToNull(draft.purchaseOrderNumber),
    fulfillmentContact: draft.fulfillmentContact ?? undefined,
    validUntil: draft.validUntilOverride,
    customAgreementReference: blankToNull(draft.customAgreementReference),
    preapprovedLegalTermIds: draft.preapprovedLegalTermIds,
    specialLicenceTerms: draft.specialLicenceTerms,
    internalNotes: blankToNull(draft.internalNotes),
    requiresManualFulfillmentApproval: draft.requiresManualFulfillmentApproval,
  };
}

export function useOrderDraft(order: OrderDto): OrderDraftState {
  const [savedOrder, setSavedOrder] = React.useState(order);
  const [draft, setDraft] = React.useState(() => toOrderDraft(order));
  const baseline = React.useMemo(() => toOrderDraft(savedOrder), [savedOrder]);

  // Why: a refetch hands back a fresh object for the same order, which must not wipe edits.
  const loadedOdNumber = React.useRef(order.odNumber);
  React.useEffect(() => {
    if (loadedOdNumber.current === order.odNumber) {
      return;
    }
    loadedOdNumber.current = order.odNumber;
    setSavedOrder(order);
    setDraft(toOrderDraft(order));
  }, [order]);

  const isDirty = !isEqual(draftContent(draft), draftContent(baseline));

  const setField = React.useCallback(
    <K extends keyof OrderDraft>(key: K, value: OrderDraft[K]) =>
      setDraft((current) => {
        const next: OrderDraft = { ...current, [key]: value };
        // Why: the API refuses a licence recipient naming a reseller the order no longer has.
        if (key === "resellerBillingAccountCode" && blankToNull(next.resellerBillingAccountCode) === null) {
          next.fulfillmentContact = withoutReseller(current.fulfillmentContact);
        }
        return next;
      }),
    []
  );

  const save = React.useCallback(async () => {
    if (!isDirty) {
      return undefined;
    }
    const saved = await updateOrder(savedOrder.odNumber, toUpdateOrderDto(draft, savedOrder.version));
    setSavedOrder(saved);
    setDraft(toOrderDraft(saved));
    return saved;
  }, [draft, isDirty, savedOrder.odNumber, savedOrder.version]);

  const reset = React.useCallback(() => setDraft(baseline), [baseline]);

  const applyServerOrder = React.useCallback((updated: OrderDto) => {
    setSavedOrder(updated);
    setDraft(toOrderDraft(updated));
  }, []);

  const applyServerOrderKeepingEdits = React.useCallback((updated: OrderDto) => setSavedOrder(updated), []);

  return { draft, setField, isDirty, save, reset, savedOrder, applyServerOrder, applyServerOrderKeepingEdits };
}
